/*
 * Smores main product (PDP) — variant picker, gallery, quantity
 *
 * <smores-product-form> is the outermost wrapper. It owns:
 *   - the variant dropdown
 *   - the hidden variant ID input
 *   - the price / compare-at / savings DOM
 *   - the Add-to-cart button text
 *
 * <smores-product-gallery> handles thumbnail clicks and main-image
 * swapping.
 *
 * Money formatting respects Shopify's setting via the global
 * `Shopify.formatMoney` if available; otherwise falls back to plain
 * USD-style "$XX.XX".
 */

(() => {
  const formatMoney = (cents, format) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && format) {
      return window.Shopify.formatMoney(cents, format);
    }
    return '$' + (cents / 100).toFixed(2);
  };

  class SmoresProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.variantSelect = this.querySelector('[data-variant-select]');
      this.variantIdInput = this.querySelector('[data-variant-id]');
      this.priceEl = this.querySelector('[data-price]');
      this.compareEl = this.querySelector('[data-compare]');
      this.savingsEl = this.querySelector('[data-savings]');
      this.atc = this.querySelector('[data-atc]');
      this.atcText = this.querySelector('[data-atc-text]');
      this.qtyInput = this.querySelector('[data-qty]');
      this.moneyFormat = this.dataset.moneyFormat;

      this.variantSelect?.addEventListener('change', () => this.updateVariant());

      this.querySelector('[data-qty-down]')?.addEventListener('click', () => this.adjustQty(-1));
      this.querySelector('[data-qty-up]')?.addEventListener('click', () => this.adjustQty(1));
    }

    updateVariant() {
      const opt = this.variantSelect.selectedOptions[0];
      if (!opt) return;

      const variantId = opt.value;
      const price = parseInt(opt.dataset.price, 10);
      const compare = opt.dataset.compare ? parseInt(opt.dataset.compare, 10) : 0;
      const available = opt.dataset.available === 'true';

      this.variantIdInput.value = variantId;

      if (this.priceEl) this.priceEl.textContent = formatMoney(price, this.moneyFormat);

      if (this.compareEl) {
        if (compare > price) {
          this.compareEl.textContent = formatMoney(compare, this.moneyFormat);
          this.compareEl.hidden = false;
        } else {
          this.compareEl.hidden = true;
        }
      }

      if (this.savingsEl) {
        if (compare > price) {
          const saved = compare - price;
          const pct = Math.round((saved / compare) * 100);
          this.savingsEl.textContent = `You save ${formatMoney(saved, this.moneyFormat)} (${pct}% off)`;
          this.savingsEl.hidden = false;
        } else {
          this.savingsEl.hidden = true;
        }
      }

      if (this.atc) {
        if (available) {
          this.atc.disabled = false;
          if (this.atcText) this.atcText.textContent = `Add To Cart - ${formatMoney(price, this.moneyFormat)}`;
        } else {
          this.atc.disabled = true;
          if (this.atcText) this.atcText.textContent = 'Sold Out';
        }
      }

      // Update URL with ?variant=ID so refresh and share preserve selection
      const url = new URL(window.location);
      url.searchParams.set('variant', variantId);
      history.replaceState({}, '', url);
    }

    adjustQty(delta) {
      const next = Math.max(1, parseInt(this.qtyInput.value, 10) + delta);
      this.qtyInput.value = String(next);
    }
  }

  class SmoresProductGallery extends HTMLElement {
    connectedCallback() {
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.images = [...this.querySelectorAll('[data-main-image]')];
      this.current = 0;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const i = parseInt(thumb.dataset.index, 10);
          this.show(i);
        });
      });

      const up = this.querySelector('[data-thumb-up]');
      const down = this.querySelector('[data-thumb-down]');
      const thumbsContainer = this.querySelector('.smores-pdp__thumbs');
      if (up && thumbsContainer) up.addEventListener('click', () => thumbsContainer.scrollBy({ top: -120, behavior: 'smooth' }));
      if (down && thumbsContainer) down.addEventListener('click', () => thumbsContainer.scrollBy({ top: 120, behavior: 'smooth' }));

      // Main-image prev/next arrows (mobile) + touch swipe
      const wrap = (i) => (i + this.images.length) % this.images.length;
      this.querySelector('[data-main-prev]')?.addEventListener('click', () => this.show(wrap(this.current - 1)));
      this.querySelector('[data-main-next]')?.addEventListener('click', () => this.show(wrap(this.current + 1)));

      const main = this.querySelector('.smores-pdp__main-image');
      let startX = null;
      main?.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      main?.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) this.show(wrap(this.current + (dx < 0 ? 1 : -1)));
        startX = null;
      }, { passive: true });

      // Only show the scroll arrows when the thumbnail strip actually overflows.
      const syncArrows = () => {
        if (!thumbsContainer) return;
        const overflowing = thumbsContainer.scrollHeight > thumbsContainer.clientHeight + 4;
        [up, down].forEach((b) => { if (b) b.style.display = overflowing ? '' : 'none'; });
      };
      syncArrows();
      window.addEventListener('resize', syncArrows);
      if ('ResizeObserver' in window) new ResizeObserver(syncArrows).observe(thumbsContainer);

      // Set the sticky offset to clear the pinned header group (the theme's
      // --header-group-height var isn't populated for this custom header).
      const setStickyTop = () => {
        const hg = document.querySelector('#header-group');
        if (hg && window.matchMedia('(min-width: 901px)').matches) {
          this.style.top = (hg.offsetHeight + 16) + 'px';
        } else {
          this.style.top = '';
        }
      };
      setStickyTop();
      window.addEventListener('resize', setStickyTop);

      this.show(0);
    }

    show(i) {
      this.current = i;
      this.thumbs.forEach((t, ti) => t.setAttribute('aria-current', String(ti === i)));
      this.images.forEach((img, ii) => img.setAttribute('data-active', String(ii === i)));
      this.thumbs[i]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
  }

  if (!customElements.get('smores-product-form'))    customElements.define('smores-product-form',    SmoresProductForm);
  if (!customElements.get('smores-product-gallery')) customElements.define('smores-product-gallery', SmoresProductGallery);
})();
